import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import './LandingPage.css';

const LandingPage: React.FC = () => {
    const { theme } = useTheme();
    const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
    const feedbacks = [
        { text: "Debriefr changed how I write forever! The AI suggestions are spot on.", author: "Sarah J., Author" },
        { text: "Finally finished my book thanks to Debriefr. The structure it provides is amazing.", author: "Michael R., First-time Writer" },
        { text: "The image generation feature brought my characters to life. Highly recommended!", author: "Emily T., Novelist" },
        { text: "Chatting with my book helps me find plot holes I never would have noticed.", author: "David K., Mystery Writer" },
        { text: "A game changer for memoir writing. It asks the right questions.", author: "Linda M., Biographer" },
        { text: "I love how easy it is to organize my chapters and ideas.", author: "James P., Blogger" },
    ];

    // Duplicate feedbacks for seamless scrolling
    const marqueeContent = [...feedbacks, ...feedbacks];

    const logoSmall = theme === 'dark'
        ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark_small.png`
        : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant_small.png`;

    const logoLarge = theme === 'dark'
        ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark.png`
        : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant.png`;

    return (
        <div className="landing-page">
            {/* Top Navigation Bar */}
            <nav className="landing-nav">
                <div className="landing-nav-content">
                    <Link to="/" className="landing-logo">
                        <img
                            src={logoSmall}
                            alt="Debriefr"
                            style={{ height: '32px', marginRight: '10px' }}
                        />
                    </Link>
                    <div className="landing-nav-links">
                        <a href="#features" className="nav-link">Features</a>
                        <a href="#pricing" className="nav-link">Pricing</a>
                        <a href="#blog" className="nav-link">Blog</a>
                        <Link to="/login" className="btn btn-nav-login">Login</Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="landing-hero">
                <div className="landing-content">
                    <img
                        src={logoLarge}
                        alt="Debriefr"
                        className="landing-hero-logo"
                        style={{ maxWidth: '400px', width: '100%', marginBottom: '1rem' }}
                    />
                    <h2 className="landing-subtitle">
                        The Best <span className="highlight-blue">AI Assisted</span> Book Writing App
                    </h2>
                    <p className="landing-description">
                        Start your book or memoir, organize chapters, generate chapter art with AI, and chat with your book to unlock your creativity.
                    </p>
                    <div className="landing-buttons">
                        <Link to="/signup" className="btn btn-primary btn-large">Get Started</Link>
                        <Link to="/login" className="btn btn-secondary btn-large">Login</Link>
                    </div>
                </div>
            </div>

            {/* Feedback Marquee */}
            <div className="feedback-section">
                <div className="marquee-container">
                    <div className="marquee-content">
                        {marqueeContent.map((feedback, index) => (
                            <div key={index} className="feedback-card">
                                <p className="feedback-text">"{feedback.text}"</p>
                                <p className="feedback-author">- {feedback.author}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Feature Showcase */}
            <div className="features-section" id="features">
                {/* Feature 1: Image Generation */}
                <div className="feature-block">
                    <div className="feature-content">
                        <h3 className="feature-title">Bring Your Stories to Life</h3>
                        <p className="feature-description">
                            Visualize your scenes and characters instantly. Generate stunning artwork based on your chapter content
                            with a single click. Perfect for inspiration, covers, or illustrated editions.
                        </p>
                        <Link to="/signup" className="btn btn-primary">Try Image Gen</Link>
                    </div>
                    <div className="feature-image-container">
                        <img
                            src={`${process.env.PUBLIC_URL}/ai_image_generation.jpg`}
                            alt="AI Image Generation"
                            className="feature-img feature-img-clickable"
                            onClick={() => setSelectedImage({ src: `${process.env.PUBLIC_URL}/ai_image_generation.jpg`, alt: 'AI Image Generation' })}
                        />
                    </div>
                </div>

                {/* Feature 2: Chat with your Book */}
                <div className="feature-block">
                    <div className="feature-content">
                        <h3 className="feature-title">Chat with Your Book</h3>
                        <p className="feature-description">
                            Have a conversation with your manuscript. Ask questions about your plot, check for inconsistencies,
                            or brainstorm what happens next. It's like having a co-author who knows every word you've written.
                        </p>
                        <Link to="/signup" className="btn btn-primary">Chat Now</Link>
                    </div>
                    <div className="feature-image-container">
                        <img
                            src={`${process.env.PUBLIC_URL}/chat_with_book.jpg`}
                            alt="Chat with Book Interface"
                            className="feature-img feature-img-clickable"
                            onClick={() => setSelectedImage({ src: `${process.env.PUBLIC_URL}/chat_with_book.jpg`, alt: 'Chat with Book Interface' })}
                        />
                    </div>
                </div>

                {/* Feature 3: AI Assisted Writing */}
                <div className="feature-block">
                    <div className="feature-content">
                        <h3 className="feature-title">AI Assisted Writing</h3>
                        <p className="feature-description">
                            Never face writer's block again. Our advanced AI helps you draft, refine, and expand your ideas.
                            Get intelligent suggestions for plot development, character arcs, and descriptive language tailored to your style.
                        </p>
                        <Link to="/signup" className="btn btn-primary">Start Writing</Link>
                    </div>
                    <div className="feature-image-container">
                        <img
                            src={`${process.env.PUBLIC_URL}/ai_improvements.jpg`}
                            alt="AI Writing Interface"
                            className="feature-img feature-img-clickable"
                            onClick={() => setSelectedImage({ src: `${process.env.PUBLIC_URL}/ai_improvements.jpg`, alt: 'AI Writing Interface' })}
                        />
                    </div>
                </div>
            </div>

            {/* Theme Toggle */}
            <div className="theme-toggle-container">
                <ThemeToggle />
            </div>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-columns">
                        {/* Learn More */}
                        <div className="footer-column">
                            <h4 className="footer-heading">Learn More</h4>
                            <ul className="footer-links">
                                <li><a href="#pricing">Pricing</a></li>
                                <li><a href="#blog">Blog</a></li>
                                <li><a href="#grammar-guide">Grammar Guide</a></li>
                                <li><a href="#tips">Debriefr Tips</a></li>
                            </ul>
                        </div>

                        {/* Company */}
                        <div className="footer-column">
                            <h4 className="footer-heading">Company</h4>
                            <ul className="footer-links">
                                <li><a href="#about">About Us</a></li>
                                <li><a href="#terms">Terms of Service</a></li>
                                <li><a href="#privacy">Privacy Policy</a></li>
                                <li><a href="#cookies">Cookie Policy</a></li>
                                <li><a href="#sitemap">Sitemap</a></li>
                            </ul>
                        </div>

                        {/* Need Support */}
                        <div className="footer-column">
                            <h4 className="footer-heading">Need Support?</h4>
                            <ul className="footer-links">
                                <li><span className="footer-text">Visit our help center</span></li>
                            </ul>
                        </div>
                    </div>

                    {/* Social Icons */}
                    <div className="footer-social">
                        <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                        </a>
                        <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                        </a>
                        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                            </svg>
                        </a>
                        <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                        </a>
                        <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                        </a>
                    </div>

                    {/* Copyright */}
                    <div className="footer-copyright">
                        <p>&copy; 2025 Debriefr Ltd. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Image Popup Modal */}
            {selectedImage && (
                <div className="image-popup-overlay" onClick={() => setSelectedImage(null)}>
                    <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
                        <img src={selectedImage.src} alt={selectedImage.alt} />
                        <button className="image-popup-close" onClick={() => setSelectedImage(null)}>×</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LandingPage;
